"use client";

import {Container} from "@/shared/components/shared";
import React, {useMemo, useState} from "react";
import Image from "next/image";
import {Button} from "@/shared/components/ui/button";
import MasonryCollage from "@/shared/components/ui/masonry-collage";
import Link from "next/link";
import CarFormModal from "@/shared/components/ui/car-form-modal";
import {useRouter} from "next/navigation";

interface CardProps {
    imageSrc: string;
    imageSrcMobile: string;
    alt: string;
    title: string;
    titleMobile: string;
    width?: number;
    height?: number;
}

const Card = ({imageSrc, imageSrcMobile, title, titleMobile, alt, width = 740, height = 296}: CardProps) => {
    return (
        <div
            className="relative group w-full aspect-[166/184] lg:aspect-[740/296] overflow-hidden rounded-[10px] lg:rounded-[20px] cursor-pointer">
            <div className="w-full h-full relative flex items-center justify-center">
                <Image
                    src={imageSrc}
                    alt={alt}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105 hidden lg:block"
                />

                <Image
                    src={imageSrcMobile}
                    alt={alt}
                    fill
                    className="object-cover lg:hidden"
                />

                <div
                    className="absolute inset-0 bg-gradient-to-b from-transparent from-34% to-[rgba(40,40,40,0.91)] to-94% mix-blend-multiply"/>

                <div className="absolute inset-0 z-10 items-end p-2 lg:p-6 hidden lg:flex">
                    <h3 className="text-white text-[18px] lg:text-[32px] uppercase leading-[1.2]"
                        dangerouslySetInnerHTML={{__html: title}}/>
                </div>
                <div className="absolute inset-0 z-10 flex items-end p-2 lg:p-6 lg:hidden">
                    <h3 className="text-white text-[18px] lg:text-[32px] uppercase leading-[1.2]"
                        dangerouslySetInnerHTML={{__html: titleMobile}}/>
                </div>
            </div>
        </div>
    )
}

const cardsData = [
    {
        imageSrc: "/images/new-level-service/one-brand.png",
        imageSrcMobile: "/images/new-level-service/one-brand-mobile-small.png",
        alt: "Одна марка",
        title: "абсолютная фокусировка<br/> на одной марке",
        titleMobile: "абсолютная фокусировка на одной марке",
    },
    {
        imageSrc: "/images/new-level-service/pit-stop.png",
        imageSrcMobile: "/images/new-level-service/pit-stop-mobile-small.png",
        alt: "Пит-стоп",
        title: "Масляный PIT STOP —<br/> как в автоспорте",
        titleMobile: "Масляный PIT STOP",
    },
    {
        imageSrc: "/images/new-level-service/mechanics.jpeg",
        imageSrcMobile: "/images/new-level-service/mechanics.jpeg",
        alt: "Команда сервиса F1",
        title: "синхронность команды f1",
        titleMobile: "синхронность команды f1",
    },
    {
        imageSrc: "/images/new-level-service/reception.png",
        imageSrcMobile: "/images/new-level-service/reception-mobile.png",
        alt: "Невероятная атмосфера",
        title: "Невероятная<br/> атмосфера",
        titleMobile: "Невероятная<br/> атмосфера",
    }
];

const images = [
    {
        src: '/images/placeholder/300x256.png',
        width: 300,
        height: 256
    },
    {
        src: '/images/placeholder/636x512.png',
        width: 636,
        height: 512
    },
    {
        src: '/images/placeholder/459x255.png',
        width: 459,
        height: 255
    },
    {
        src: '/images/placeholder/431x256.png',
        width: 431,
        height: 256
    },
    {
        src: '/images/placeholder/420x257.png',
        width: 420,
        height: 257
    },
    {
        src: '/images/placeholder/551x256.png',
        width: 551,
        height: 256
    },
    {
        src: '/images/placeholder/459x257.png',
        width: 459,
        height: 257
    },
]

export default function AutoFest() {
    const router = useRouter();
    const [state, setState] = useState({
        isModalFormOpen: false,
    });

    const stateValues = useMemo(() => ({
        isModalFormOpen: state.isModalFormOpen,
    }), [state]);

    const {
        isModalFormOpen
    } = stateValues;
    const toggleModalForm = () => {
        setState(prev => ({...prev, isModalFormOpen: !prev.isModalFormOpen}));
    };

    const handleClose = () => {
        router.push('/');
    };

    return (
        <>
            <section className={'mt-[89px]'}>
                <Container className={'pt-11 lg:pt-31'}>
                    <div className={'flex items-start justify-between gap-4'}>
                        <h1 className={'uppercase font-bold text-[30px] lg:text-[70px] leading-[1.2] text-custom-black flex-1'}>
                            Добро пожаловать<br/>
                            <span className={'text-custom-orange'}>в мир AutoFEST!</span>
                        </h1>
                        <button
                            onClick={handleClose}
                            className="flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity self-start"
                            aria-label="Закрыть"
                        >
                            <svg 
                                width="40" 
                                height="40" 
                                viewBox="0 0 40 40" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                className={'w-10 h-10 lg:w-16 lg:h-16'}
                            >
                                <path 
                                    d="M10 10L30 30M30 10L10 30" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round"
                                    className="text-custom-light-gray"
                                />
                            </svg>
                        </button>
                    </div>
                    <p className={'uppercase text-[18px] lg:text-[45px] leading-[1.2] mt-5 lg:mt-7'}>
                        Мы приглашаем вас стать участниками особого ритуала,<br/>
                        где обслуживание автомобиля становится настоящим перформансом.
                    </p>
                </Container>
            </section>
            <section>
                <Container className={'pt-8 lg:pt-36'}>
                    <h2 className={'font-bold uppercase text-[30px] lg:text-[70px] leading-[1.2] text-custom-black'}>что
                        вас ждет:</h2>
                    <div className={'grid grid-cols-2 gap-4 lg:gap-6 mt-4 lg:mt-16'}>
                        {cardsData.map((card, index) => (
                            <Card key={index} imageSrc={card.imageSrc} alt={card.alt}
                                  imageSrcMobile={card.imageSrcMobile} title={card.title}
                                  titleMobile={card.titleMobile}/>
                        ))}
                    </div>
                </Container>
            </section>
            <section>
                <Container className={'pt-12 lg:pt-31 pb-31 lg:text-center'}>
                    <h2 className={'uppercase font-bold text-[30px] lg:text-[70px] leading-[1.2] text-custom-black'}>
                        замена масла выгоднее,<br/>
                        <span className={'text-custom-orange'}>чем в обычный день</span>
                    </h2>
                    <h3 className={'uppercase text-[18px] lg:text-[45px] leading-[1.2] mt-5 lg:mt-7 text-custom-black'}>
                        <span className={'font-bold'}>Пит-стоп</span> – завораживает. На это приятно смотреть. <br className={'hidden lg:block'}/>
                        Попробуйте этот мир.
                    </h3>
                    <p className={'text-[18px] lg:text-[24px] leading-[1.1] text-custom-black mt-2 lg:mt-13'}>
                        Мир AutoFest – праздник для вашего автомобиля, на котором точно стоит побывать!
                    </p>
                    <div className={'flex flex-col items-center'}>
                        <Link href={'/schedule-autofest'} className="w-full flex justify-center">
                            <Button
                                variant="filledOrange"
                                className="font-button font-bold uppercase py-2 lg:py-3 px-4 lg:px-14 mt-8 lg:mt-17 w-full max-w-[360px] lg:max-w-[500px]"
                            >
                                узнать расписание фестивалей
                            </Button>
                        </Link>
                        <Button
                            variant="filledOrange"
                            className="font-button font-bold uppercase py-3 lg:py-3 px-4 lg:px-14 mt-8 lg:mt-17 leading-[1.0] w-full max-w-[360px] lg:max-w-[500px]"
                            onClick={toggleModalForm}
                        >
                            Зарегистрироваться<br/> на следующий фестиваль
                        </Button>
                    </div>
                </Container>
            </section>
            <section className={'bg-custom-dark-gray'}>
                <Container className={'pt-8 pb-8 lg:pt-12 lg:pb-12'}>
                    <h2 className={'text-white uppercase text-[18px] lg:text-[45px] leading-[1.2] font-bold'}>
                        посмотрите, как это было
                    </h2>
                </Container>
            </section>
            <h3 className={'uppercase text-[18px] lg:text-[45px] leading-[1.2] mt-8 lg:mt-12 mb-8 lg:mb-12 text-custom-black text-center'}>
                скоро здесь появятся фотографии с фестивалей!
                <br/>Не пропустите
            </h3>
            {/*<MasonryCollage images={images} />*/}
            <CarFormModal title={'Зарегистрироваться на следующий фестиваль'} isOpen={isModalFormOpen}
                          onClose={toggleModalForm} showCarFields={true}/>
        </>
    );
};